import Vue from 'vue'
import moment from 'moment'
import { throttle } from 'throttle-debounce'
import ja from 'vee-validate/dist/locale/ja'
import VeeValidate from 'vee-validate'
import getAuthHost from '@/utils/getAuthHost'
import { FORM_LABELS } from '@/CONSTS'

const authServer = getAuthHost()
let axiosConfig = {
  timeout: 8000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'content-type': 'application/json'
  }
}

const axiosInstance = axios.create(axiosConfig)

// バリデーションのエラーメッセージ管理
const dictionary = {
  ja: {
    messages: {
      alpha_dash: () => '英数字とハイフン、アンダースコアのみ使用できます。',
      alpha_num: () => '半角英数字でご入力ください。',
      alpha_spaces: () => 'アルファベットと空白のみ使用できます。',
      alpha: () => '全てアルファベットで入力してください',
      confirmed: field => `${FORM_LABELS[field] || field}が一致しません。`,
      credit_card: () => 'クレジットカード番号が正しくありません。',
      email: () => 'メールアドレスの形式が有効ではありません。',
      ext: (field, [fileName]) => `${fileName}は有効なファイル形式ではありません。`,
      image: (field, [fileName]) => `${fileName}は有効な画像形式ではありません。`,
      mimes: (field, [fileName]) => `${fileName}は有効なファイル形式ではありません。`,
      max: (field, [length]) => `${length}文字以内でご入力ください。`,
      min: (field, [length]) => `${FORM_LABELS[field]}は${length}文字以上でご入力ください。`,
      max_value: (field, [max]) => `${max}以下でご入力ください。`,
      min_value: (field, [min]) => `${FORM_LABELS[field] || 'こちらの項目'}は${min}以上でご入力ください。`,
      not_in: () => '不正な値です。',
      numeric: () => '半角数字でご入力ください。',
      regex: () => '指定の形式でご入力ください。',
      url: () => 'URLの形式が有効ではありません。',
      required: field => `${FORM_LABELS[field] || 'こちらの項目'}を入力してください`
    }
  }
}

Vue.use(VeeValidate, {
  fieldsBagName: 'formFields',
  locale: 'ja',
  dictionary
})

const { Validator } = VeeValidate

// カスタムバリデーション

/**
 * 生年月日の範囲設定(1940-01-01から今日の間でないとInvalid)
 */
Validator.extend('birthRange', {
  getMessage() {
    return '生年月日は1940年1月1日から今日の間でご入力ください。'
  },
  validate(value) {
    const format = date => `${date.substr(0, 4)}-${date.substr(4, 2)}-${date.substr(6, 2)}`
    const now = moment()
    const oldest = moment('1940-01-01')
    const formated = moment(format(value))
    //８文字以外はエラー
    if (value.length < 8 || value.length > 8) {
      return false
    }
    if (/[12][0-9]{3}([1][0-2]|[0][1-9])(0[1-9]|[12][0-9]|3[01])/.test(value)) {
      return (formated.isBefore(now) && formated.isAfter(oldest)) || formated.isSame(now.format('YYYY-MM-DD')) || formated.isSame(oldest)
    }
    return false
  }
})
/**
 * 登録メールのRFC準拠チェック
 */
Validator.extend('nonRfcEmailCheck', {
  getMessage() {
    return 'メールアドレスの形式が有効ではありません。'
  },
  validate(value) {
    if (value) {
      let checkFlag = /^[a-zA-Z0-9\.\?\+\/_-]+\@[a-zA-Z0-9_-]+[a-zA-Z0-9\._-]+$/
      if (checkFlag.test(value)) {
        return true
      } else {
        return false
      }
    }
    return false
  }
})

let duplicateEmail_message = `会員登録済みのメールアドレスです。こちらから<a href="/user/login?show=signin">ログイン</a>してください`
/**
 * 登録時メールアドレスの重複チェック
 */
Validator.extend('duplicateEmail', {
  getMessage() {
    return duplicateEmail_message
  },
  validate(email) {
    if (email) {
      return new Promise(resolve => {
        return throttle(
          300,
          axios
            .post(`${authServer}/api/user/emailcheck`, {
              email
            })
            .then(res => {
              // success
              if (res.data.response === 'NG') {
                resolve({ valid: false })
              } else {
                resolve({ valid: true })
              }
            })
            // 重複メールチェックが動かなかった時
            .catch(error => {
              duplicateEmail_message =
                '只今、メールアドレスの変更をすることができません。申し訳ありませんが、時間を置いて再度お試しください。'
              resolve({ valid: false })
            })
        )
      })
    } else {
      return true
    }
  }
})

/**
 * パスワード入力チェック
 */
Validator.extend('passCheck1', {
  getMessage() {
    return '半角英字と数字を組み合わせたものを入力してください。'
  },
  validate(pass) {
    let checkFlag1 = /^(?!.*?(?=.*?[a-z])(?=.*?\d)).+$/i
    let checkFlag2 = /[^a-z\d!-~！”＃＄％＆’（）＊＋，－．／：；＜＝＞？＠［￥］＾＿‘｛｜｝￣、。・゛゜´｀¨ヽヾゝゞ〃仝々〆〇ー―‐＼～〜∥…‥“〔〕〈〉《》「」『』【】±×÷≠≦≧∞∴♂♀°′″℃￠￡§☆★○●◎◇◇◆□■△▲▽▼※〒→←↑↓〓¥\uD800-\uDBFF\uDC00-\uDFFF]|[\s ]/i
    let checkFlag3 = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z\-].+$/

    return !checkFlag1.test(pass) && !checkFlag2.test(pass)
  }
})

/**
 * パスワード入力チェック
 */
Validator.extend('passCheck2', {
  getMessage() {
    return '使用できない記号が含まれています。'
  },
  validate(pass) {
    let checkFlag = /[！”＃＄％＆’（）＊＋，－．／：；＜＝＞？＠［￥］＾＿‘｛｜｝￣、。・゛゜´｀¨ヽヾゝゞ〃仝々〆〇ー―‐＼～〜∥…‥“〔〕〈〉《》「」『』【】±×÷≠≦≧∞∴♂♀°′″℃￠￡§☆★○●◎◇◇◆□■△▲▽▼※〒→←↑↓〓¥\uD800-\uDBFF\uDC00-\uDFFF]/

    return !checkFlag.test(pass)
  }
})

/**
 * パスワード入力チェック
 */
Validator.extend('passCheck3', {
  getMessage() {
    return '使用できない単語が含まれています。'
  },
  validate(pass) {
    let checkFlag = /^(?=.*(password|qwerty|abc|admin|root|123)).+$/i

    return !checkFlag.test(pass)
  }
})

/**
 * 時間入力フォーマット
 */
Validator.extend('birthtimeFormat', {
  getMessage() {
    return '正しく入力してください'
  },
  validate(value) {
    if (!value) {
      return
    }
    let regEx = /(([0-1][0-9]|2[0-3])([0-5][0-9]))|9999/
    return regEx.test(value)
  }
})

/**
 * 記号が入力されていないかチェック
 */
Validator.extend('symbolCheck', {
  getMessage: () => '記号が含まれているお名前は占うことができません',
  validate: value =>
    value
      ? !/[!-/！”＃＄％＆’（）＊＋，－．／：；＜＝＞？＠［￥］＾＿‘｛｜｝￣,、.。・゛゜´｀¨ヽヾゝ〃仝〆〇ー―‐＼～〜∥…‥“〔〕〈〉《》「」『』【】±×÷≠≦≧∞∴♂♀°′″℃￠￡§☆★○●◎◇◇◆□■△▲▽▼※〒→←↑↓〓¥\uD800-\uDBFF\uDC00-\uDFFF]/.test(
          value
        )
      : true
})

/**
 * 英字が入力されていないかチェック
 */
Validator.extend('alphabetCheck', {
  getMessage: () => '英字が含まれているお名前は占うことができません',
  validate: value => (value ? !/[a-zA-Zａ-ｚＡ-Ｚ]/.test(value) : true)
})

/**
 * 半角カナが入力されていないかチェック
 */
Validator.extend('halfKanaCheck', {
  getMessage: () => '半角カナは占うことができません。全角カナへ変更してください',
  validate: value => (value ? !/[ｦ-ﾟ]/.test(value) : true)
})

/**
 * 数字が入力されていないかチェック
 */
Validator.extend('numberCheck', {
  getMessage: () => '数字が含まれているお名前は占うことができません',
  validate: value => (value ? !/[0-9０-９]/.test(value) : true)
})

/**
 * ひらがなチェック
 */
Validator.extend('hiraganaOnly', {
  getMessage: () => 'ひらがなで入力してください',
  validate: value => (value ? /^[ぁ-ろわ-ゞー]+$/.test(value) : true)
})
/**
 * 恋愛状況の選択チェック
 */
Validator.extend('relationshipCheck', {
  getMessage() {
    return '恋愛状況を選択してください'
  },
  validate(value) {
    if (value == '0') {
      return false
    } else {
      return true
    }
  }
})



// WEBPACK FOOTER //
// ./resources/assets/js/utils/validatorConfig.js